/**
 * RecruitingNoteViewerModal
 * Read-only modal for viewing a recruiting note authored on mobile.
 * Handwritten notes render the image; text notes render the content.
 */

import { useEffect, useRef} from "react";
import { useDialogFocus } from "../../../../../hooks/useDialogFocus";
import { createPortal } from "react-dom";
import type { RecruitingNoteResponse } from "../../../../../types/api";
import { useFreshRecruitingNoteUrls } from "../../../../../hooks/useFreshRecruitingNoteUrls";
import { ModalCloseButton } from "../../../../../components/ModalCloseButton";
import { Button } from "../../../../../components/Button";
import "./RecruitingNoteViewerModal.css";

interface RecruitingNoteViewerModalProps {
  note: RecruitingNoteResponse | null;
  onClose: () => void;
  /**
   * Offers "Notify a coach" on a player's note. The host closes this viewer and opens the staff
   * dialog itself: two focus-trapping dialogs open at once fight over Tab.
   */
  onNotify?: (note: RecruitingNoteResponse) => void;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export function RecruitingNoteViewerModal({
  note,
  onClose,
  onNotify,
}: RecruitingNoteViewerModalProps) {

  const isOpen = note !== null;

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!note) return null;

  return <RecruitingNoteViewerModalBody note={note} onClose={onClose} onNotify={onNotify} />;
}

interface RecruitingNoteViewerModalBodyProps {
  note: RecruitingNoteResponse;
  onClose: () => void;
  onNotify?: (note: RecruitingNoteResponse) => void;
}

/**
 * Separated so the signed-URL refresh hook only mounts while there's an
 * actual note to view — the parent's `note | null` prop would otherwise force
 * a conditional hook call.
 */
function RecruitingNoteViewerModalBody({
  note,
  onClose,
  onNotify,
}: RecruitingNoteViewerModalBodyProps) {
  // Declared here, not in the wrapper: the dialog element lives in this
  // component, and the wrapper only mounts it once a note is selected.
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus({ isOpen: true, dialogRef, handleEscape: false });

  const timestamp = note.updatedAt || note.createdAt;
  // Notes are polymorphic: prospect-scoped (prospectId set) or team-scoped
  // roster annotations (teamId set, prospectId/prospectName null). Never assume
  // a prospect — every field below tolerates the team-note shape [SpecFlow E7].
  const isTeamNote = note.prospectId == null && note.teamId != null;
  const headingLabel =
    note.title ||
    (isTeamNote
      ? "Roster annotation"
      : note.noteType === "handwritten"
        ? "Handwritten note"
        : "Note");
  const { imageUrl, isUnavailable, handleImageError } = useFreshRecruitingNoteUrls(note);
  const isHandwritten = note.noteType === "handwritten";

  // Rendered into document.body via a portal. The viewer relies on
  // `position: fixed` to cover the viewport, but it's mounted deep inside the
  // prospect viewer panel — and that panel uses `transform` for its slide-in,
  // which makes it the containing block for fixed descendants. Without the
  // portal the overlay/modal would be trapped (and clipped by the panel's
  // `overflow: hidden`) instead of centered on screen.
  return createPortal(
    <>
      <div className="recruiting-note-viewer__overlay" onClick={onClose} />
      <div ref={dialogRef} tabIndex={-1}
        className="recruiting-note-viewer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="recruiting-note-viewer-title"
      >
        <div className="recruiting-note-viewer__header">
          <h2
            id="recruiting-note-viewer-title"
            className="recruiting-note-viewer__title"
          >
            {headingLabel}
          </h2>
          <div className="recruiting-note-viewer__header-actions">
            {onNotify && note.prospectId && (
              <Button variant="secondary" onClick={() => onNotify(note)}>
                Notify a coach
              </Button>
            )}
            <ModalCloseButton onClick={onClose} />
          </div>
        </div>

        <div className="recruiting-note-viewer__meta">
          {formatDate(timestamp)}
          {note.createdByName ? ` · ${note.createdByName}` : ""}
        </div>

        <div className="recruiting-note-viewer__body">
          {isHandwritten && imageUrl && !isUnavailable ? (
            <img
              src={imageUrl}
              alt={note.title || "Handwritten note"}
              className="recruiting-note-viewer__image"
              onError={handleImageError}
            />
          ) : isHandwritten && imageUrl && isUnavailable ? (
            <div
              className="recruiting-note-viewer__image recruiting-note-viewer__image--unavailable"
              role="img"
              aria-label="Image unavailable"
            >
              Image unavailable
            </div>
          ) : (
            <p className="recruiting-note-viewer__text">
              {note.content || "Empty note"}
            </p>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}
